<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('ADD_SANCTIONED_ID')"
            :label="$t('Add')"
            size="md"
            @click="showModal('modal-lg')"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="blackListRef"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :items="sactionScreeningData"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>
      <template #cell(message)="data">
        {{ data.value.length > 25 ? `${data.value.substring(0, 25)}...` : data.value }}
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item @click="onSelectSanctionScreeningItem(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50"> {{ $t('View') }} </span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      no-close-on-backdrop
      size="lg"
      :title="modalTitle"
      :ok-disabled="isLoading"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="sanctionScreeningRules">
        <b-form>
          <b-row class="match-height">
            <b-col md="6">
              <SelectField
                id="id-type"
                v-model="sactionScreeningFormData.idType"
                :label="$t('ID Type')"
                :placeholder="$t('Select')"
                :options="translatedSactionScreeningIDTypeOptions"
                :reduce="option => option.value"
                item="label"
                :error-name="$t('ID Type')"
                rules="required"
                :readonly="isReadonly"
                :set-selected="setIDType"
              />
            </b-col>
            <b-col md="6">
              <InputField
                id="id-number"
                v-model="sactionScreeningFormData.idNumber"
                :label="idNumberLabel"
                :placeholder="idNumberLabel"
                type="text"
                :error-name="idNumberLabel"
                rules="required"
                :readonly="isReadonly"
              />
            </b-col>
            <b-col md="12">
              <TextAreaField
                id="message"
                v-model="sactionScreeningFormData.message"
                :label="$t('Message')"
                :placeholder="$t('Enter Message')"
                :error-name="$t('Message')"
                rules="required"
                rows="5"
                :readonly="isReadonly"
              />
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
      <FullScreenSpinner :show="showSpinner" />
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchSactionScreeningList"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <b-col md="6">
          <SelectField
            id="id-type"
            v-model="filter.idType"
            :label="$t('ID Type')"
            :placeholder="$t('Select')"
            :options="translatedSactionScreeningIDTypeOptions"
            :reduce="option => option.value"
            item="label"
            :readonly="isReadonly"
          />
        </b-col>
        <b-col md="6">
          <InputField
            id="id-number"
            v-model="filter.idNumber"
            :label="idNumberLabel"
            :placeholder="idNumberLabel"
            type="text"
            for="id-number"
            :readonly="isReadonly"
          />
        </b-col>

      </b-row>
    </b-modal>
    <!-- Filter modal -->
    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BTable,
  BSpinner,
  BModal,
  BForm,
  BDropdownItem,
  BDropdown,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import getStatusColor from '@/helpers/getStatusColor'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import { fraudMonitorPermissions } from '@/helpers/permissions'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../forms/InputField.vue'
import SelectField from '../../forms/SelectField.vue'
import TextAreaField from '../../forms/TextAreaField.vue'
import Button from '../../components/button/Button.vue'

const sactionScreeningIDTypeOptions = [
  { value: 'BVN', label: 'BVN' },
  { value: 'NIN', label: 'NIN' },
  { value: 'TIN', label: 'TIN' },
  { value: 'PhoneNumber', label: 'Phone Number' },
  { value: 'Email', label: 'Email' },
  { value: 'DeviceID', label: 'Device ID' },
]

export default {
  components: {
    BCard,
    BRow,
    BCol,
    SelectField,
    BTable,
    BModal,
    BForm,
    BSpinner,
    ValidationObserver,
    InputField,
    BDropdownItem,
    BDropdown,
    TablePagination,
    Button,
    TextAreaField,
    TableEntries,
    FullScreenSpinner,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      query: '',
      getStatusColor,
      isReadonly: false,
      isEditable: false,
      showSpinner: false,
      sortBy: 'name',
      buttonLabel: this.$t('Save'),
      required,
      modalTitle: this.$t('Create Sanction Screening'),
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'idType', sortable: true, label: 'ID Type' },
        { key: 'idNumber', sortable: true, label: 'Number' },
        { key: 'message', sortable: true, label: 'Message' },
        { key: 'dateCreated', sortable: true, label: 'Date Created' },
        { key: 'actions' },
      ],
      sactionScreeningFormData: {
        idType: '',
        idNumber: '',
        message: '',
      },
      filter: {
        idType: '',
        idNumber: '',
      },
      idNumberLabel: this.$t('Number'),
      // sactionScreeningIDTypeOptions: [
      //   { value: 'BVN', label: 'BVN' },
      //   { value: 'NIN', label: 'NIN' },
      //   { value: 'TIN', label: 'TIN' },
      //   { value: 'PhoneNumber', label: 'Phone Number' },
      //   { value: 'Email', label: 'Email' },
      //   { value: 'DeviceID', label: 'Device ID' },
      // ],
      sactionScreeningData: [],
    }
  },
  computed: {
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedSactionScreeningIDTypeOptions() {
      return sactionScreeningIDTypeOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchSactionScreeningList()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchSactionScreeningList()
    },
  },
  mounted() {
    this.fetchSactionScreeningList()
    this.fetchAllCurrencies()
    this.fetchCASAProducts()
    this.fetchTransactionRules()
  },
  methods: {
    ...mapActions({
      getSanctionScreening: 'fraudmonitor/getSanctionScreening',
      createSanctionScreening: 'fraudmonitor/createSanctionScreening',
    }),

    setIDType(idType) {
      const idLabels = {
        BVN: this.$t('BVN Number'),
        NIN: this.$t('NIN Number'),
        TIN: this.$t('TIN Number'),
        PhoneNumber: this.$t('Phone Number'),
        Email: this.$t('Email Address'),
        DeviceID: this.$t('Device ID'),
      }
      this.idNumberLabel = idLabels[idType] || this.$('Number')
    },

    async fetchSactionScreeningList() {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getSanctionScreening({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: fraudMonitorPermissions.VIEW_SANCTIONED_LIST,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.sactionScreeningData = data.data
          this.totalCount = data.recordCount
        } else {
          this.sactionScreeningData = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async handleSubmit(bvModalEvt) {
      try {
        bvModalEvt.preventDefault()
        this.$refs.sanctionScreeningRules.validate().then(async success => {
          if (success) {
            const text1 = this.$t('Are you sure you want to')
            const text2 = this.$t('Sanction Screening?')
            this.confirm(this.sanctionScreeningActions, {
              text: `${text1} ${this.buttonLabel} ${text2}`,
            })
          }
        })
      } catch (error) {
        this.hideModal('modal-lg')
        this.alertError({ text: error })
      } finally {
        this.hideModal('modal-lg')
      }
    },
    async sanctionScreeningActions() {
      try {
        this.isLoading = true
        this.showSpinner = true
        const formData = {
          ...this.sactionScreeningFormData,
        }
        const response = await this.createSanctionScreening({ formData, permission: fraudMonitorPermissions.ADD_SANCTIONED_ID })
        const { status } = await response
        if (status === 200) {
          this.fetchSactionScreeningList()
          this.hideModal('modal-lg')
          const requestText = this.$t('Your request to ')
          const successText = this.$t('Sanction Screening was successful')
          this.alertSuccess({
            text: `${requestText} ${this.buttonLabel} ${successText}`,
          })
          this.showSpinner = false
        }
      } catch (error) {
        this.hideModal('modal-lg')
        const requestText = this.$t('Your request to ')
        const reasonText = this.$t('Sanction Screening failed. Reason:')
        error.response.data.errors.forEach(responseError => {
          this.alertError({ text: `${requestText} ${this.buttonLabel} ${reasonText} ${responseError.errorMessage}` })
        })
      } finally {
        this.hideModal('modal-lg')
        this.showSpinner = false
      }
    },
    async onSelectSanctionScreeningItem(items, activity, val) {
      this.sactionScreeningFormData.idType = items.idType
      this.sactionScreeningFormData.idNumber = items.idNumber
      this.sactionScreeningFormData.message = items.message
      this.sactionScreeningFormData.id = items.id
      this.isReadonly = val === 'view' ? this.isReadonly = true : this.isReadonly = false
      this.modalTitle = val === 'view' ? this.$t('View Sanction Screening') : this.$t('Create Sanction Screening')
      const idLabels = {
        BVN: this.$t('BVN Number'),
        NIN: this.$t('NIN Number'),
        TIN: this.$t('TIN Number'),
        PhoneNumber: this.$t('Phone Number'),
        Email: this.$t('Email Address'),
        DeviceID: this.$t('Device ID'),
      }
      this.idNumberLabel = idLabels[items.idType] || this.$t('Number')
      this.$root.$emit('bv::show::modal', 'modal-lg')
    },
    resetModal() {
      this.sactionScreeningFormData = { ...null }
      this.idNumberLabel = this.$t('Number')
      this.isLoading = false
      this.isReadonly = false
      this.modalTitle = this.$t('Create Sanction Screening')
      this.buttonLabel = this.$t('Save')
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchSactionScreeningList()
    },
  },
}
</script>
